import { useTranslation } from "react-i18next";

import { Button, Grid, InputAdornment } from "@mui/material";

import { FormDialog } from "../../../../../../../../form-dialog/FormDialog";
import { FormikDecimalTextField } from "../../../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../formik/FormikYesNoAutocomplete";
import { FormikDatePicker } from "../../../../../../../../formik/formik-date-picker/FormikDatePicker";
import { OwnClaimField, OwnClaimFormValue, OwnClaimsMaxLength } from "../../../../OwnClaimsView.types";
import { useOwnClaimDialog } from "./hooks/useOwnClaimDialog";

const TAB = "clients.client.debtSettlement.assetInventory.ownClaimsTab";
const SECTION = `${TAB}.detail`;

const HALF = { xs: 12, md: 6 };
const FULL = 12;

interface Props {
  claim: OwnClaimFormValue;
  number: number;
  onSave: (claim: OwnClaimFormValue) => void;
  onDelete?: () => void;
  onClose: () => void;
}

export function OwnClaimDialog({ claim, number, onSave, onDelete, onClose }: Props) {
  const { t } = useTranslation();
  const { formik } = useOwnClaimDialog(claim, onSave, onClose);
  const values = formik.values;
  const fieldId = (field: OwnClaimField) => `own-claim-${claim.key}-${field}`;

  const yesNo = (field: OwnClaimField, label: string) => (
    <FormikYesNoAutocomplete id={fieldId(field)} formik={formik} fieldName={field} label={label} />
  );

  const text = (field: OwnClaimField, label: string, maxLength: number, multiline = false) => (
    <FormikTextField
      id={fieldId(field)}
      formik={formik}
      fieldName={field}
      label={label}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      slotProps={{ input: { inputProps: { maxLength } } }}
    />
  );

  return (
    <FormDialog
      title={t(`${SECTION}.title`, { number })}
      onClose={onClose}
      formik={formik}
      submitLabel={t(`${SECTION}.apply`)}
      extraAction={
        onDelete && {
          left: (
            <Button variant="outlined" color="error" onClick={onDelete} disabled={formik.isSubmitting}>
              {t("common.delete")}
            </Button>
          ),
        }
      }
      paperSx={{ width: 1100 }}
    >
      <Grid container spacing={2} pt={1}>
        <Grid size={HALF}>{text("debtorName", t(`${SECTION}.debtorName`), OwnClaimsMaxLength.debtorName)}</Grid>
        <Grid size={HALF}>
          {text("debtorAddress", t(`${SECTION}.debtorAddress`), OwnClaimsMaxLength.debtorAddress)}
        </Grid>

        <Grid size={HALF}>{yesNo("isRelated", t(`${SECTION}.isRelated`))}</Grid>
        {values.isRelated === true && (
          <Grid size={HALF}>{text("relationship", t(`${SECTION}.relationship`), OwnClaimsMaxLength.relationship)}</Grid>
        )}

        <Grid size={FULL}>{text("legalReason", t(`${SECTION}.legalReason`), OwnClaimsMaxLength.legalReason)}</Grid>

        <Grid size={HALF}>
          <FormikDecimalTextField
            id={fieldId("amount")}
            formik={formik}
            fieldName="amount"
            label={t(`${SECTION}.amount`)}
            decimalScale={2}
            minValue={0}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
          />
        </Grid>
        <Grid size={HALF}>
          <FormikDatePicker formik={formik} fieldName="dueDate" label={t(`${SECTION}.dueDate`)} />
        </Grid>

        <Grid size={FULL}>{yesNo("isSecured", t(`${SECTION}.isSecured`))}</Grid>
        {values.isSecured === true && (
          <Grid size={FULL}>{text("securities", t(`${SECTION}.securities`), OwnClaimsMaxLength.text, true)}</Grid>
        )}

        <Grid size={HALF}>{yesNo("isDisputed", t(`${SECTION}.isDisputed`))}</Grid>

        <Grid size={HALF}>
          {values.isDisputed === true &&
            yesNo("hasPendingCourtProceedings", t(`${SECTION}.hasPendingCourtProceedings`))}
        </Grid>
        {values.isDisputed === true && values.hasPendingCourtProceedings !== null && (
          <Grid size={FULL}>
            {text(
              "disputeDetails",
              t(values.hasPendingCourtProceedings ? `${SECTION}.courtProceedingDetails` : `${SECTION}.disputeDetails`),
              OwnClaimsMaxLength.text,
              true
            )}
          </Grid>
        )}

        <Grid size={FULL}>{yesNo("isCollectible", t(`${SECTION}.isCollectible`))}</Grid>
        {values.isCollectible === false && (
          <Grid size={FULL}>
            {text("collectionSteps", t(`${SECTION}.collectionSteps`), OwnClaimsMaxLength.text, true)}
          </Grid>
        )}
      </Grid>
    </FormDialog>
  );
}
